/*! hod-menu.js — Home Outlet Direct mega menu (desktop) + menu drawer levels (mobile).
 *  Plain ES2017, no dependencies, loaded with `defer`. DOM contract: design_handoff_mega_menu/_extract/ARCH.md §3.
 *
 *  Desktop (.hod-nav[data-hm-nav])
 *    - mouseenter on an item with a panel opens it (switches if another is open); mouseenter on an
 *      item without a panel closes an unpinned panel.
 *    - hover intent (9/24): once a panel is showing, a NEIGHBOURING item or rail row takes over only
 *      after the pointer has rested on it (HOVER_DWELL_MS), and not while the pointer is heading for
 *      the open content (menu-aim triangle: bar → the panel below, rail → the tile pane to the right);
 *      leaving the region closes an unpinned panel after CLOSE_GRACE_MS unless the pointer comes back.
 *    - click / Enter / Space on an item with a panel pins it; a second click on the pinned item closes.
 *    - scrim click and Esc close + unpin; leaving the nav-bar + panel region closes an unpinned panel.
 *    - rail rows ([data-hm-group]) switch tile groups ([data-hm-tiles]) on mouseenter and click (touch and
 *      keyboard: first activation previews, second follows data-hm-href); the selection resets to group 0
 *      whenever the department changes.
 *    - touch: the synthetic mouseenter that follows a tap is ignored so the tap reaches the click
 *      handler exactly once and pins the panel.
 *  Mobile (.hod-mm[data-hm-mobile] inside the theme drawer #mobile-menu)
 *    - [data-hm-open="i"] swaps root -> detail i, [data-hm-back] returns, [data-hm-chip="i"] swaps tile
 *      groups (and points the header's [data-hm-viewall] at the group's data-hm-va-url — 9/18);
 *      the drawer resets to root whenever #mobile-menu gets aria-hidden="true".
 *  Live chat: any [data-hm-chat] opens Tidio; if the Tidio API is absent 3s after init every
 *    [data-hm-chat] gets the `hidden` attribute (and <html> gets .hod-chat-missing for CSS).
 *
 *  Never binds theme.js selectors (data-type="menuitem"/"menu", data-action, aria-hidden on panels).
 */
(function () {
  'use strict';

  /* Re-entry guard: script included twice, or the header section re-rendered in the theme editor. */
  if (window.HODMenu && typeof window.HODMenu.init === 'function') {
    window.HODMenu.init(document);
    return;
  }

  var READY_ATTR = 'data-hm-ready';
  var TOUCH_GRACE_MS = 800;   /* ignore synthetic mouse events this long after a touch pointerdown */
  var CHAT_WAIT_MS = 3000;    /* how long to wait for window.tidioChatApi before hiding [data-hm-chat] */
  var MOBILE_MQ = '(max-width: 999px)'; /* theme switches to the mobile header below 1000px */
  /* Hover intent (Stas 9/24): a diagonal move from a department to its sidebar rail, or from a low
     rail row up to a tile, crosses the neighbours in between — none of them may take over on the way.
     (1) dwell: with a panel showing, a neighbouring department / rail row must hold the pointer this
         long before it switches; the first open stays instant.
     (2) aim: while the pointer keeps moving toward the open content the switch keeps waiting — the
         menu-aim triangle (Amazon / Kamens): last position → the two corners of the content's near
         edge, corners pushed out by AIM_TOLERANCE. It resumes when the pointer stops or turns away.
     (3) grace: leaving the nav-bar + panel region closes an unpinned panel this long later, unless
         the pointer is back inside by then. */
  var HOVER_DWELL_MS = 160;
  var AIM_TOLERANCE = 75;
  var CLOSE_GRACE_MS = 150;
  var AIM_WINDOW_MS = 120;    /* the direction vector spans the pointer samples of the last 120 ms (at least the last two):
                                 a slow, shallow drift quantises to dy = 0 over two or three samples and would read as level */
  var AIM_REST_MS = 60;       /* a newest sample older than this means the pointer has stopped (a moving pointer reports every
                                 frame) — the aim rule then stands down, whatever direction the last vector had (review 9/24) */
  var MOUSE_LOCS = 12;        /* pointer samples kept (≈ 100-200 ms at typical mousemove rates) */

  var desktops = [];
  var mobiles = [];
  var docBound = false;
  var lastTouchAt = 0;
  var chatState = 'unknown';  /* 'unknown' | 'ready' | 'missing' */
  var chatTimer = null;

  /* ------------------------------------------------------------------ helpers */

  function toArray(list) { return Array.prototype.slice.call(list || []); }
  function qsa(root, sel) { return toArray(root.querySelectorAll(sel)); }
  function isEl(n) { return !!n && n.nodeType === 1; }
  function esc(v) { return String(v).replace(/["\\]/g, ''); }
  function recentTouch() { return (Date.now() - lastTouchAt) < TOUCH_GRACE_MS; }
  function inRect(r, x, y) { return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom; }
  function side(p, a, b) { return (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y); }
  function inTriangle(p, a, b, c) {
    var d1 = side(p, a, b), d2 = side(p, b, c), d3 = side(p, c, a);
    return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
  }
  function attached(el) { return !!el && document.documentElement.contains(el); }

  /* Panels and drawer levels start display:none, and Chrome never fetches a loading="lazy" image that
     was hidden at layout time until a real scroll/resize nudges it — tiles, featured cards and brand
     logos stayed blank after a panel opened. Flip the lazy images that are (or just became) visible to
     eager the moment their container shows. Tile groups that are still hidden are left lazy. */
  function wakeImages(root) {
    if (!isEl(root)) return;
    qsa(root, 'img[loading="lazy"]').forEach(function (img) {
      var grp = img.closest('[data-hm-tiles], [data-hm-mtiles]');
      if (grp && !grp.classList.contains('is-on')) return;
      img.loading = 'eager';
    });
  }

  /* Toggle `is-on` so that exactly the element whose `attr` equals `idx` is on. */
  function setOn(list, attr, idx) {
    var want = String(idx);
    list.forEach(function (el) {
      var on = el.getAttribute(attr) === want;
      el.classList.toggle('is-on', on);
      if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ desktop */

  function Desktop(nav) {
    this.nav = nav;
    this.scrim = nav.querySelector('[data-hm-scrim]');
    this.open = null;      /* department index (string) or null */
    this.pinned = false;
    this.locs = [];        /* last MOUSE_LOCS pointer positions inside the nav (aim test) */
    this.pend = null;      /* a hover switch waiting for its dwell: { fire, el, timer, last } */
    this.closeTimer = null;/* the close-grace timer */
    this.bind();
    this.resetDom();
  }

  Desktop.prototype.itemFor = function (idx) {
    return this.nav.querySelector('[data-hm-item="' + esc(idx) + '"]');
  };

  Desktop.prototype.panelFor = function (idx) {
    return this.nav.querySelector('[data-hm-panel="' + esc(idx) + '"]');
  };

  Desktop.prototype.hasPanel = function (item) {
    return item.hasAttribute('data-hm-item') && !!this.panelFor(item.getAttribute('data-hm-item'));
  };

  /* Put every trigger/panel/scrim into the closed state (fresh init or editor re-render). */
  Desktop.prototype.resetDom = function () {
    qsa(this.nav, '[data-hm-item]').forEach(function (item) {
      item.setAttribute('aria-expanded', 'false');
      item.classList.remove('is-open');
    });
    qsa(this.nav, '[data-hm-panel]').forEach(function (panel) { panel.hidden = true; });
    if (this.scrim) this.scrim.hidden = true;
  };

  Desktop.prototype.hidePanel = function (idx) {
    var item = this.itemFor(idx);
    var panel = this.panelFor(idx);
    if (panel) panel.hidden = true;
    if (item) {
      item.classList.remove('is-open');
      item.setAttribute('aria-expanded', 'false');
    }
  };

  /* Open department `idx` (switching instantly from any other). Does not change `pinned`. */
  Desktop.prototype.show = function (idx) {
    idx = String(idx);
    var item = this.itemFor(idx);
    var panel = this.panelFor(idx);
    if (!item || !panel) return;
    this.cancelPending();
    this.cancelClose();
    if (this.open === idx) return;                /* already showing: keep the rail selection */
    if (this.open !== null) this.hidePanel(this.open);
    this.open = idx;
    panel.hidden = false;
    item.classList.add('is-open');
    item.setAttribute('aria-expanded', 'true');
    this.selectGroup(panel, 0);                   /* activeGroupIndex resets on department change */
    wakeImages(panel);
    if (this.scrim) this.scrim.hidden = false;
  };

  /* Close the open panel. Without `unpin` a pinned panel stays open (hover / focus / leave rules). */
  Desktop.prototype.close = function (unpin) {
    this.cancelPending();
    this.cancelClose();
    if (!unpin && this.pinned) return;
    this.pinned = false;
    if (this.open === null) return;
    this.hidePanel(this.open);
    this.open = null;
    if (this.scrim) this.scrim.hidden = true;
  };

  /* Does this trigger carry a real department URL (so a second click can follow it)? */
  Desktop.prototype.navigable = function (item) {
    if (!item || item.tagName !== 'A') return false;
    var href = item.getAttribute('href');
    return !!href && href !== '#' && href.charAt(0) !== '#';
  };

  /* Click path (mouse, touch tap, Enter, Space): pin; a second click on the pinned item follows its
     link when it has one (see the click handler) and otherwise closes the panel. */
  Desktop.prototype.toggle = function (idx) {
    idx = String(idx);
    if (this.open === idx && this.pinned) {
      this.close(true);
      return;
    }
    this.show(idx);
    this.pinned = true;
  };

  /* Esc: close + unpin, returning focus to the trigger when focus was inside the panel. */
  Desktop.prototype.escape = function () {
    if (this.open === null) return;
    var item = this.itemFor(this.open);
    var panel = this.panelFor(this.open);
    var refocus = !!panel && !!document.activeElement && panel.contains(document.activeElement);
    this.close(true);
    if (refocus && item && typeof item.focus === 'function') item.focus();
  };

  /* Sidebar layout: select rail row `g` and its tile group inside `panel`. */
  Desktop.prototype.selectGroup = function (panel, g) {
    var rows = qsa(panel, '[data-hm-group]');
    if (!rows.length) return;
    setOn(rows, 'data-hm-group', g);
    var groups = qsa(panel, '[data-hm-tiles]');
    setOn(groups, 'data-hm-tiles', g);
    groups.forEach(function (grp) { if (grp.classList.contains('is-on')) wakeImages(grp); });
  };

  /* Is the pointer over the nav bar or the open panel? The scrim is a child of .hod-nav, so the
     region's mouseleave never fires while the pointer is over it; this is the geometric fallback. */
  Desktop.prototype.pointerInRegion = function (x, y) {
    if (inRect(this.nav.getBoundingClientRect(), x, y)) return true;
    var panel = this.open !== null ? this.panelFor(this.open) : null;
    return !!panel && inRect(panel.getBoundingClientRect(), x, y);
  };

  /* ---- hover intent (see the constants) */

  Desktop.prototype.trackPointer = function (x, y) {
    this.locs.push({ x: x, y: y, t: Date.now() });
    if (this.locs.length > MOUSE_LOCS) this.locs.shift();
  };

  /* Is the pointer's latest movement aimed at `rect`? `from` names the side the pointer approaches
     from: 'top' (the bar above a panel) or 'left' (the rail beside its tile pane). */
  Desktop.prototype.aimedAt = function (rect, from) {
    var n = this.locs.length;
    if (!rect || n < 2 || !(rect.width > 0)) return false;
    var loc = this.locs[n - 1], prev = this.locs[n - 2];
    for (var i = n - 3; i >= 0 && loc.t - this.locs[i].t <= AIM_WINDOW_MS; i--) prev = this.locs[i];
    if (loc.x === prev.x && loc.y === prev.y) return false;
    var a, b;
    if (from === 'left') {
      a = { x: rect.left, y: rect.top - AIM_TOLERANCE };
      b = { x: rect.left, y: rect.bottom + AIM_TOLERANCE };
    } else {
      a = { x: rect.left - AIM_TOLERANCE, y: rect.top };
      b = { x: rect.right + AIM_TOLERANCE, y: rect.top };
    }
    return inTriangle(loc, prev, a, b);
  };

  Desktop.prototype.cancelPending = function () {
    if (!this.pend) return;
    clearTimeout(this.pend.timer);
    this.pend = null;
  };

  /* Drop the pending switch when it belongs to `el` (the pointer left it before the dwell was up). */
  Desktop.prototype.cancelIf = function (el) {
    if (this.pend && this.pend.el === el) this.cancelPending();
  };

  /* Run `fire` once the pointer has dwelt on `el` for HOVER_DWELL_MS — and, while it keeps moving
     toward the open content (`rectFn()` approached from `from`), keep waiting another dwell. A rest,
     a move away, or leaving `el` (cancelIf) settles it. "Resting" is judged by the age of the newest
     sample, not by whether one arrived since the last check: mouseenter fires before the mousemove
     that carried the pointer in, so one fresh-looking sample always lands right after defer(). */
  Desktop.prototype.defer = function (fire, el, rectFn, from) {
    var self = this;
    this.cancelPending();
    var p = { fire: fire, el: el, timer: null, last: this.locs[this.locs.length - 1] || null };
    var check = function () {
      if (self.pend !== p) return;
      var loc = self.locs[self.locs.length - 1] || null;
      var resting = !loc || loc === p.last || (Date.now() - loc.t) >= AIM_REST_MS;
      p.last = loc;
      if (!resting && self.aimedAt(rectFn(), from)) { p.timer = setTimeout(check, HOVER_DWELL_MS); return; }
      self.pend = null;
      fire();
    };
    p.timer = setTimeout(check, HOVER_DWELL_MS);
    this.pend = p;
  };

  /* Close an unpinned panel after the grace, unless cancelClose() runs first. */
  Desktop.prototype.closeSoon = function () {
    var self = this;
    if (this.open === null || this.pinned || this.closeTimer) return;
    this.closeTimer = setTimeout(function () {
      self.closeTimer = null;
      self.close(false);
    }, CLOSE_GRACE_MS);
  };

  Desktop.prototype.cancelClose = function () {
    if (!this.closeTimer) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  };

  Desktop.prototype.bind = function () {
    var self = this;
    var nav = this.nav;

    /* Touch: remember the tap so the synthetic mouseenter/mouseleave that follow it are ignored;
       the tap then reaches the click handler once and pins (README: first tap opens, pinned). */
    nav.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') lastTouchAt = Date.now();
    }, true);

    /* Pointer history for the aim test; a pending close is called off once the pointer is back in
       the nav-bar + panel region (the scrim is inside .hod-nav, so mouseenter alone cannot tell). */
    nav.addEventListener('mousemove', function (e) {
      self.trackPointer(e.clientX, e.clientY);
      if (self.closeTimer && self.pointerInRegion(e.clientX, e.clientY)) self.cancelClose();
    });
    nav.addEventListener('mouseenter', function () { self.cancelClose(); });

    var openPanelRect = function () {
      var panel = self.open !== null ? self.panelFor(self.open) : null;
      return panel ? panel.getBoundingClientRect() : null;
    };

    qsa(nav, '.hod-nav__item').forEach(function (item) {
      var withPanel = self.hasPanel(item);
      var idx = item.getAttribute('data-hm-item');

      item.addEventListener('mouseenter', function () {
        if (recentTouch()) return;
        self.cancelClose();
        if (withPanel && self.open === idx) { self.cancelPending(); return; }
        if (self.open === null) {                 /* nothing showing: the first open is instant */
          self.cancelPending();
          if (withPanel) self.show(idx);
          return;
        }
        /* another panel is showing: dwell (1) + aim (2) before this item takes over — or, for a
           plain link (About, Sale), before it closes the panel */
        self.defer(function () {
          if (withPanel) self.show(idx);
          else self.close(false);
        }, item, openPanelRect, 'top');
      });
      item.addEventListener('mouseleave', function () { self.cancelIf(item); });

      if (!withPanel) return;   /* plain links (About, Sale) navigate natively */

      item.addEventListener('click', function (e) {
        if (e.defaultPrevented) return;
        /* Modifier-clicks / middle-clicks keep the department URL usable in a new tab. */
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        /* Stas 9/6: hover already previews the panel, so a mouse click simply follows the department
           link (one click → the collection). Touch has no hover: the first tap opens the panel, the
           second tap on the same department follows the link. */
        if (self.navigable(item)) {
          if (!recentTouch()) return;                                   /* mouse / keyboard: navigate */
          if (self.open === idx && self.pinned) return;                 /* touch, already open: navigate */
        }
        e.preventDefault();
        self.toggle(idx);
      });

      item.addEventListener('keydown', function (e) {
        /* Enter fires a native click on <a> (handled above); Space does not. */
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          self.toggle(idx);
        }
      });
    });

    /* Rail rows (sidebar layout): mouseenter + focus preview the tile group; a
       click GOES to the group's page (data-hm-href = the pane's "View all" URL —
       Stas 9/14) so nobody has to travel to the right-hand link. Touch has no
       hover, so there the first tap previews and the second tap goes — the same
       rule the department items use. Cmd/Ctrl/middle click open a new tab. */
    qsa(nav, '[data-hm-panel]').forEach(function (panel) {
      qsa(panel, '[data-hm-group]').forEach(function (row) {
        var g = row.getAttribute('data-hm-group');
        var href = row.getAttribute('data-hm-href');
        var touch = false;
        var pick = function () { self.cancelPending(); self.selectGroup(panel, g); };
        var paneRect = function () {
          var pane = panel.querySelector('[data-hm-tiles].is-on');
          return pane ? pane.getBoundingClientRect() : null;
        };
        /* hover: dwell (1) + aim at the tile pane (2) before the pane swaps; the row that is already
           on just clears a pending swap (the pointer came back). The tap's synthetic mouseenter is
           ignored like the departments' so the two-step click rule below sees the first tap. */
        row.addEventListener('mouseenter', function () {
          if (recentTouch()) return;
          if (row.classList.contains('is-on')) { self.cancelPending(); return; }
          self.defer(pick, row, paneRect, 'left');
        });
        row.addEventListener('mouseleave', function () { self.cancelIf(row); });
        row.addEventListener('pointerdown', function (e) { touch = e.pointerType === 'touch'; });
        row.addEventListener('click', function (e) {
          e.preventDefault();
          if (!href) { pick(); return; }
          /* touch AND keyboard (Enter/Space arrive as clicks with detail 0) use the two-step rule: the
             first activation previews the pane, the second follows the link — so Tab can travel through
             the rail without swapping panes and a keyboard user can reach the pane they picked (9/18). */
          var twoStep = touch || e.detail === 0;
          if (twoStep && !row.classList.contains('is-on')) { pick(); return; }
          pick();
          if (e.metaKey || e.ctrlKey || e.shiftKey) { window.open(href, '_blank', 'noopener'); return; }
          /* 10/7 (Stas): on collection pages assets/hod-softnav.js swaps the next collection in place — same hook the
             tiles use; it hard-loads by itself when the target cannot be swapped (Sale template, errors). */
          if (window.hodSoftNav && typeof window.hodSoftNav.go === 'function') {
            var su = null; try { su = new URL(href, location.href); } catch (err) { su = null; }
            if (su && su.origin === location.origin && /^\/collections\//.test(su.pathname)) { closeAll(); window.hodSoftNav.go(su.href); return; }
          }
          window.location.href = href;
        });
        row.addEventListener('auxclick', function (e) {
          if (e.button === 1 && href) { e.preventDefault(); window.open(href, '_blank', 'noopener'); }
        });
      });
    });

    if (this.scrim) {
      this.scrim.addEventListener('click', function () { self.close(true); });
      var onScrimPointer = function (e) {
        if (recentTouch()) return;
        if (self.pointerInRegion(e.clientX, e.clientY)) self.cancelClose();
        else self.closeSoon();                    /* (3) grace, not an instant close */
      };
      this.scrim.addEventListener('mouseenter', onScrimPointer);
      this.scrim.addEventListener('mousemove', onScrimPointer);
    }

    /* Mouseleave of the nav-bar + panel region (panels and scrim are descendants of .hod-nav). */
    nav.addEventListener('mouseleave', function () {
      if (recentTouch()) return;
      self.cancelPending();
      self.closeSoon();                           /* (3) grace, not an instant close */
    });

    /* Keyboard: focus moving out of the nav region closes an unpinned panel. */
    nav.addEventListener('focusout', function (e) {
      var to = e.relatedTarget;
      if (isEl(to) && !nav.contains(to)) self.close(false);
    });
  };

  /* ------------------------------------------------------------------ mobile */

  function Mobile(root) {
    this.root = root;
    this.scroll = root.querySelector('.hod-mm__scroll');
    this.current = null;   /* index of the open detail, or null at root */
    this.observer = null;
    this.bind();
    this.goRoot();
  }

  Mobile.prototype.details = function () {
    return qsa(this.root, '[data-hm-detail]');
  };

  Mobile.prototype.scrollToTop = function () {
    if (this.scroll) this.scroll.scrollTop = 0;
  };

  Mobile.prototype.focusSafely = function (el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  };

  /* Level 1. `restoreFocus` moves focus back to the row that opened the detail (keyboard/SR users). */
  Mobile.prototype.goRoot = function (restoreFocus) {
    var rootLevel = this.root.querySelector('[data-hm-root]');
    var opener = this.current !== null
      ? this.root.querySelector('[data-hm-open="' + esc(this.current) + '"]')
      : null;
    this.details().forEach(function (d) { d.hidden = true; });
    if (rootLevel) rootLevel.hidden = false;
    this.root.setAttribute('data-hm-level', 'root');
    qsa(this.root, '[data-hm-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    this.current = null;
    if (restoreFocus) this.focusSafely(opener);
    this.scrollToTop();
  };

  /* Level 2 for department `idx`: in-place swap, chips reset to 0, scroll to top. */
  Mobile.prototype.openDetail = function (idx) {
    var detail = this.root.querySelector('[data-hm-detail="' + esc(idx) + '"]');
    if (!detail) return;
    var rootLevel = this.root.querySelector('[data-hm-root]');
    this.details().forEach(function (d) { d.hidden = (d !== detail); });
    if (rootLevel) rootLevel.hidden = true;
    this.root.setAttribute('data-hm-level', 'detail');
    qsa(this.root, '[data-hm-open]').forEach(function (b) { b.setAttribute('aria-expanded', b.getAttribute('data-hm-open') === String(idx) ? 'true' : 'false'); });
    this.current = String(idx);
    this.selectChip(detail, 0);
    wakeImages(detail);
    this.focusSafely(detail.querySelector('[data-hm-back]'));
    this.scrollToTop();
  };

  /* Chips + tiles layout: select chip `i` and its tile group inside `detail`. */
  Mobile.prototype.selectChip = function (detail, i) {
    var chips = qsa(detail, '[data-hm-chip]');
    if (!chips.length) return;
    setOn(chips, 'data-hm-chip', i);
    /* 9/27 review: the rail keeps its horizontal scroll across back / reopen, so the freshly selected chip could sit off-screen —
       nudge the rail so the active chip is fully visible (no vertical side effects, unlike scrollIntoView). */
    var onChip = null;
    chips.forEach(function (c) { if (c.getAttribute('data-hm-chip') === String(i)) onChip = c; });
    var rail = onChip ? onChip.parentElement : null;
    if (onChip && rail && rail.scrollWidth > rail.clientWidth) {
      var pad = 18;
      var left = onChip.offsetLeft - rail.offsetLeft;
      var right = left + onChip.offsetWidth;
      if (left - pad < rail.scrollLeft) rail.scrollLeft = Math.max(0, left - pad);
      else if (right + pad > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = right + pad - rail.clientWidth;
    }
    var groups = qsa(detail, '[data-hm-mtiles]');
    setOn(groups, 'data-hm-mtiles', i);
    var on = null;
    groups.forEach(function (grp) { if (grp.classList.contains('is-on')) { on = grp; wakeImages(grp); } });
    /* 9/18 sidebar-sectional: the header's "View all" follows the active group when the group names a
       target (data-hm-va-url / -label). Groups without one leave the department link in place. */
    var va = detail.querySelector('[data-hm-viewall]');
    if (va && on && on.hasAttribute('data-hm-va-url')) {
      var url = on.getAttribute('data-hm-va-url');
      var label = on.getAttribute('data-hm-va-label') || '';
      if (url) { va.setAttribute('href', url); va.hidden = false; } else { va.hidden = true; }
      if (label) va.setAttribute('aria-label', 'View all ' + label);
    }
  };

  Mobile.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var t = e.target;
      if (!isEl(t) || typeof t.closest !== 'function') return;
      var el;
      if ((el = t.closest('[data-hm-open]'))) {
        e.preventDefault();
        self.openDetail(el.getAttribute('data-hm-open'));
        return;
      }
      if ((el = t.closest('[data-hm-back]'))) {
        e.preventDefault();
        self.goRoot(true);
        return;
      }
      if ((el = t.closest('[data-hm-chip]'))) {
        e.preventDefault();
        self.selectChip(el.closest('[data-hm-detail]') || self.root, el.getAttribute('data-hm-chip'));
      }
    });

    /* The theme toggles aria-hidden on #mobile-menu; reset to root whenever it closes. */
    var drawer = this.root.closest('#mobile-menu') || document.getElementById('mobile-menu');
    if (drawer && window.MutationObserver) {
      this.observer = new MutationObserver(function () {
        if (drawer.getAttribute('aria-hidden') === 'true') self.goRoot();
      });
      this.observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  };

  /* ------------------------------------------------------------------ live chat (Tidio) */

  function openChat() {
    var api = window.tidioChatApi;
    if (!api) return;
    try {
      if (typeof api.show === 'function') api.show();
      if (typeof api.open === 'function') api.open();
    } catch (err) { /* Tidio not ready yet; nothing to do */ }
  }

  function setChatHidden(hide) {
    qsa(document, '[data-hm-chat]').forEach(function (el) { el.hidden = hide; });
    document.documentElement.classList.toggle('hod-chat-missing', hide);
  }

  function chatReady() {
    chatState = 'ready';
    if (chatTimer) { clearTimeout(chatTimer); chatTimer = null; }
    setChatHidden(false);
  }

  /* Runs on every init so controls rendered later (editor re-render) get the known state. */
  function checkChat() {
    if (window.tidioChatApi) { chatReady(); return; }
    if (chatState === 'missing') { setChatHidden(true); return; }
    if (chatTimer) return;
    chatTimer = setTimeout(function () {
      chatTimer = null;
      if (window.tidioChatApi) { chatReady(); return; }
      chatState = 'missing';
      setChatHidden(true);
    }, CHAT_WAIT_MS);
  }

  /* ------------------------------------------------------------------ document-level wiring (once) */

  function closeAll() {
    desktops.forEach(function (d) { d.close(true); });
  }

  function desktopFor(node) {
    for (var i = 0; i < desktops.length; i++) {
      if (desktops[i].nav.contains(node)) return desktops[i];
    }
    return null;
  }

  function bindDocument() {
    if (docBound) return;
    docBound = true;

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      desktops.forEach(function (d) { d.escape(); });
    });

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!isEl(t) || typeof t.closest !== 'function') return;
      var chat = t.closest('[data-hm-chat]');
      if (!chat) return;
      e.preventDefault();
      openChat();
    });

    document.addEventListener('tidioChat-ready', chatReady);

    /* Theme editor: re-init on section re-render; open a department's panel when its block is selected. */
    document.addEventListener('shopify:section:load', function (e) {
      init(isEl(e.target) ? e.target : document);
    });
    document.addEventListener('shopify:block:select', function (e) {
      var t = e.target;
      if (!isEl(t) || typeof t.closest !== 'function') return;
      var panel = t.closest('[data-hm-panel]') || t.querySelector('[data-hm-panel]');
      var d = panel ? desktopFor(panel) : null;
      if (!d) return;
      d.show(panel.getAttribute('data-hm-panel'));
      d.pinned = true;
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      var t = e.target;
      if (!isEl(t) || typeof t.closest !== 'function') return;
      if (t.closest('[data-hm-panel]') || t.querySelector('[data-hm-panel]')) closeAll();
    });

    /* Crossing into the mobile breakpoint hides the desktop nav via CSS; drop its open state too. */
    if (window.matchMedia) {
      var mq = window.matchMedia(MOBILE_MQ);
      var onChange = function (m) { if (m.matches) closeAll(); };
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
      else if (typeof mq.addListener === 'function') mq.addListener(onChange);
    }
  }

  /* ------------------------------------------------------------------ init (idempotent) */

  function init(scope) {
    var root = isEl(scope) ? scope : document;

    /* Drop controllers whose DOM was replaced (theme editor re-render). */
    desktops = desktops.filter(function (d) { return attached(d.nav); });
    mobiles = mobiles.filter(function (m) {
      if (attached(m.root)) return true;
      if (m.observer) m.observer.disconnect();
      return false;
    });

    var navs = qsa(root, '[data-hm-nav]');
    if (root !== document && root.matches && root.matches('[data-hm-nav]')) navs.unshift(root);
    navs.forEach(function (nav) {
      if (nav.hasAttribute(READY_ATTR)) return;
      nav.setAttribute(READY_ATTR, '');
      desktops.push(new Desktop(nav));
    });

    var drawers = qsa(root, '[data-hm-mobile]');
    if (root !== document && root.matches && root.matches('[data-hm-mobile]')) drawers.unshift(root);
    drawers.forEach(function (el) {
      if (el.hasAttribute(READY_ATTR)) return;
      el.setAttribute(READY_ATTR, '');
      mobiles.push(new Mobile(el));
    });

    bindDocument();
    checkChat();
  }

  window.HODMenu = {
    version: '1.2.0',
    init: init,
    closeAll: closeAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
